import type { SolutionPageContent } from '../../types/solution'

export const understandPerformanceSolution: SolutionPageContent = {
  slug: 'understand-performance',
  hero: {
    headline: 'Know how your business is doing — today, not at month end',
    subheadline: 'Sales, profit, and cash flow in clear reports, built from the work you already do.',
    card: {
      type: 'bars',
      title: 'Profit · last 6 months',
      value: 'RWF 2.4M',
      delta: { label: '+14%', tone: 'positive' },
      bars: [
        { label: 'Apr', value: 1.6 },
        { label: 'May', value: 1.8 },
        { label: 'Jun', value: 1.7 },
        { label: 'Jul', value: 2.0 },
        { label: 'Aug', value: 2.1 },
        { label: 'Sep', value: 2.4, highlight: true },
      ],
    },
  },
  challenges: [
    {
      quote: 'I’m busy every day, but I don’t know if I’m making money.',
      detail: 'Sales feel good, but the bank balance tells a different story.',
    },
    {
      quote: 'I only see the numbers when my accountant sends them.',
      detail: 'By then it’s too late to change anything that month.',
    },
    {
      quote: 'I can’t tell which days, products, or staff perform best.',
      detail: 'Decisions are based on gut feeling, not facts.',
    },
  ],
  helps: [
    {
      solves: 'I don’t know if I’m making money',
      title: 'Profit and loss, always up to date',
      copy: 'Kayko combines sales, costs, and expenses into a live profit and loss report, so you always know where you stand.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Profit & loss · September',
          rows: [
            { label: 'Revenue', value: 'RWF 8,420,000' },
            { label: 'Cost of goods', value: '− RWF 4,960,000' },
            { label: 'Expenses', value: '− RWF 1,060,000' },
          ],
          footer: { label: 'Net profit', value: 'RWF 2,400,000' },
        },
      },
    },
    {
      solves: 'I only see the numbers from my accountant',
      title: 'Cash flow you can check anytime',
      copy: 'See money coming in and going out, week by week. Spot a tight month coming before it arrives.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'bars',
          title: 'Net cash flow · weekly',
          value: 'RWF 640K',
          delta: { label: 'This week', tone: 'brand' },
          bars: [
            { label: 'W1', value: 0.4 },
            { label: 'W2', value: 0.7 },
            { label: 'W3', value: 0.3 },
            { label: 'W4', value: 0.64, highlight: true },
          ],
        },
      },
    },
    {
      solves: 'I can’t tell what performs best',
      title: 'Performance by product, day, and staff',
      copy: 'Compare best-selling products, busiest days, and team performance so you can plan stock and shifts with confidence.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Sales by staff · September',
          rows: [
            { label: 'Aline U.', value: 'RWF 3.2M', pct: 100, tone: 'positive' },
            { label: 'Eric N.', value: 'RWF 2.7M', pct: 84 },
            { label: 'Claude M.', value: 'RWF 2.5M', pct: 78 },
          ],
        },
      },
    },
  ],
  journey: {
    heading: 'How it works',
    subheading: 'Reports that build themselves from your daily work.',
    items: [
      { title: 'Record sales', copy: 'Every sale is captured at checkout.' },
      { title: 'Record costs', copy: 'Purchases and expenses add the other side.' },
      { title: 'Kayko does the maths', copy: 'Profit, margins, and cash flow calculated automatically.' },
      { title: 'Decide with confidence', copy: 'Check reports on your phone, anytime.' },
    ],
  },
  results: [
    { value: 'Daily', label: 'profit visibility instead of monthly' },
    { value: '+14%', label: 'higher profit after acting on product reports' },
    { value: '1 tap', label: 'to export reports for your accountant' },
  ],
  testimonial: {
    quote: 'I check my profit every evening on my phone. I know which products to push and when to hold back on spending.',
    name: 'Jean-Paul Niyonzima',
    role: 'Owner',
    business: 'Mizero Café & Bakery',
    location: 'Kimihurura, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/2c23e8d6-e530-4eee-9f47-5e53b680c437.jpg',
    metric: { value: '+14%', label: 'higher profit in six months' },
  },
  products: ['reports', 'sales-pos', 'expenses'],
  plan: { name: 'Starter', audience: 'owners who want clear reports' },
  faq: [
    {
      question: 'Which reports are included?',
      answer: 'Daily sales on every plan. Profit and loss from Starter, and advanced analytics and branch reports from Growth.',
    },
    {
      question: 'Do I need accounting knowledge?',
      answer: 'No. Reports are written in plain language, with clear totals and simple charts.',
    },
    {
      question: 'Can my accountant access the reports?',
      answer: 'Yes. Invite your accountant with read-only access, or export reports as spreadsheets or PDF.',
    },
    {
      question: 'How up to date are the numbers?',
      answer: 'Reports update as you sell and record costs. Offline sales appear once your device reconnects.',
    },
    {
      question: 'Can I compare periods?',
      answer: 'Yes. Compare this week, month, or year with the previous one.',
    },
  ],
  related: ['control-expenses', 'manage-multiple-locations', 'retail'],
  cta: {
    headline: 'See your business clearly, every day',
    subheadline: 'Start with your first sale. Local support in Kigali when you need it.',
  },
}
